// ============================================================================
// @energyos/roof-core · export.ts — Dachgeometrie als DXF / GeoJSON exportieren
// ----------------------------------------------------------------------------
// Für die Übergabe an Installateur/CAD (pvXpert exportiert in den Webshop/CAD).
// Reine, deterministische String-Erzeugung — KEIN DB/API/Zufall.
//
// • DXF: minimaler, weit kompatibler AutoCAD-R12-ENTITIES-Block mit geschlossenen
//   POLYLINEs je Polygon, getrennt nach Layer. Koordinaten in METERN (CAD-Frame:
//   x = Ost, y = Nord) — die Aufrufseite projiziert LatLng → lokale Meter.
// • GeoJSON: FeatureCollection mit LatLng-Polygonen (lng,lat-Reihenfolge) +
//   `kind`-Property je Feature.
// Clean-room (DXF/GeoJSON sind offene Formate; selbst implementiert).
// ============================================================================
const NL = "\r\n"; // DXF nutzt CRLF
function pair(code, value) {
    return `${code}${NL}${value}`;
}
/**
 * Erzeugt eine vollständige DXF-R12-Datei (HEADER-frei, nur ENTITIES) mit je
 * einer geschlossenen POLYLINE pro Eingabe-Polyline. Mind. 2 Punkte je Polyline.
 */
export function polygonsToDxf(polylines) {
    const lines = [];
    lines.push(pair(0, "SECTION"));
    lines.push(pair(2, "ENTITIES"));
    for (const pl of polylines) {
        const pts = (pl.points ?? []).filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
        if (pts.length < 2)
            continue;
        const layer = pl.layer || "0";
        const color = pl.color ?? 7;
        lines.push(pair(0, "POLYLINE"));
        lines.push(pair(8, layer));
        lines.push(pair(62, color));
        lines.push(pair(66, 1)); // vertices follow
        lines.push(pair(70, 1)); // 1 = geschlossen
        for (const p of pts) {
            lines.push(pair(0, "VERTEX"));
            lines.push(pair(8, layer));
            lines.push(pair(10, p.x));
            lines.push(pair(20, p.y));
            lines.push(pair(30, 0));
        }
        lines.push(pair(0, "SEQEND"));
        lines.push(pair(8, layer));
    }
    lines.push(pair(0, "ENDSEC"));
    lines.push(pair(0, "EOF"));
    return lines.join(NL) + NL;
}
function ring(poly) {
    const r = poly
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
        .map((p) => [p.lng, p.lat]);
    // GeoJSON-Ring muss geschlossen sein.
    const first = r[0];
    const last = r[r.length - 1];
    if (first && last && (first[0] !== last[0] || first[1] !== last[1]))
        r.push(first);
    return r;
}
function feature(poly, kind) {
    if ((poly?.length ?? 0) < 3)
        return null;
    // Erst den (gefilterten + geschlossenen) Ring bilden: non-finite Punkte fallen in
    // ring() weg. Ein geschlossener, gültiger Polygon-Ring braucht ≥ 4 Positionen
    // (3 distinkte + Schluss-Wiederholung). Andernfalls verwerfen — sonst würde ein
    // Polygon mit length ≥ 3 aber NaN/Infinity-Koordinaten zu einem entarteten Ring
    // kollabieren und trotzdem ausgegeben.
    const r = ring(poly);
    if (r.length < 4)
        return null;
    return {
        type: "Feature",
        properties: { kind },
        geometry: { type: "Polygon", coordinates: [r] },
    };
}
/** Baut eine GeoJSON-FeatureCollection aus Grundriss + Dach-/Störflächen. */
export function roofDesignToGeoJSON(input) {
    const features = [];
    if (input.footprint) {
        const f = feature(input.footprint, "building");
        if (f)
            features.push(f);
    }
    for (const r of input.roofs ?? []) {
        const f = feature(r, "roof");
        if (f)
            features.push(f);
    }
    for (const o of input.obstacles ?? []) {
        const f = feature(o, "obstacle");
        if (f)
            features.push(f);
    }
    return { type: "FeatureCollection", features };
}
// ── LatLng → lokale Meter (equirektangulär um einen Bezugspunkt) ──────────────
const R_EARTH = 6371000;
const toRad = (d) => (d * Math.PI) / 180;
/** Projiziert LatLng-Polygone auf lokale Meter (x=Ost, y=Nord) um den Zentroid aller Punkte. */
export function latLngPolysToLocalMeters(polys) {
    const all = polys.flat().filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    const ref = all.length > 0
        ? {
            lat: all.reduce((a, p) => a + p.lat, 0) / all.length,
            lng: all.reduce((a, p) => a + p.lng, 0) / all.length,
        }
        : { lat: 0, lng: 0 };
    const cosLat = Math.cos(toRad(ref.lat));
    const local = polys.map((poly) => poly.map((p) => ({
        x: toRad(p.lng - ref.lng) * cosLat * R_EARTH,
        y: toRad(p.lat - ref.lat) * R_EARTH,
    })));
    return { local, ref };
}
//# sourceMappingURL=export.js.map